import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, Grid, Paper, IconButton, Chip } from '@mui/material';
import { FaChevronLeft, FaChevronRight, FaEye } from 'react-icons/fa';
import { productService } from '../services/api';
import { useNavigate } from 'react-router-dom';

// Number of preview products (excluding newest highlighted)
const PREVIEW_COUNT = 3;

const CategoryShowcase = ({ categories }) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [categoryProducts, setCategoryProducts] = useState({}); // {categoryId: { newest, preview }}
  const [loadingMap, setLoadingMap] = useState({});
  const [catWindowStart, setCatWindowStart] = useState(0); // window start for bottom category buttons (size 5)

  const current = categories[index] || null;

  const resolveImage = (raw) => {
    if (!raw) return 'https://via.placeholder.com/140x140?text=Produit';
    // Absolute or data URIs should pass through unchanged
    if (/^https?:\/\//i.test(raw) || /^data:/i.test(raw)) return raw;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return `${base.replace(/\/$/, '')}/${raw.replace(/^\//, '')}`;
  };

  const fetchProductsForCategory = useCallback(async (catId) => {
    if (!catId) return;
    if (categoryProducts[catId]) return; // already fetched
    try {
      setLoadingMap(prev => ({ ...prev, [catId]: true }));
      const res = await productService.getByCategory(catId);
      const all = (res.data || []).map(p => ({
        ...p,
        _displayImage: resolveImage(p.image || (p.images && p.images[0]))
      }));
      if (all.length === 0) {
        setCategoryProducts(prev => ({ ...prev, [catId]: { newest: null, preview: [] } }));
      } else {
        // newest by highest id (fallback first element)
        const newest = all.reduce((a, b) => (a == null || (b.id || 0) > (a.id || 0)) ? b : a, null) || all[0];
        const pool = all.filter(p => p !== newest);
        // shuffle pool
        const shuffled = [...pool];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const preview = shuffled.slice(0, PREVIEW_COUNT);
        setCategoryProducts(prev => ({ ...prev, [catId]: { newest, preview } }));
      }
    } catch (e) {
      console.error('Failed to load products for category', catId, e);
      setCategoryProducts(prev => ({ ...prev, [catId]: { newest: null, preview: [] } }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [catId]: false }));
    }
  }, [categoryProducts]);

  // Prefetch first category
  useEffect(() => {
    if (current) fetchProductsForCategory(current.id);
  }, [current, fetchProductsForCategory]);

  const handlePrev = () => {
    setIndex(i => (i === 0 ? categories.length - 1 : i - 1));
  };
  const handleNext = () => {
    setIndex(i => (i === categories.length - 1 ? 0 : i + 1));
  };

  const dataForCurrent = current ? categoryProducts[current.id] : null;
  const previewProducts = dataForCurrent?.preview || [];
  const newestProduct = dataForCurrent?.newest || null;
  const loading = current ? loadingMap[current.id] : false;

  // Helper to ensure window contains a given index
  const ensureWindowContains = useCallback((targetIndex) => {
    const total = categories.length || 1;
    const size = Math.min(5, total);
    let start = catWindowStart;
    const inWindow = (i) => {
      const end = (start + size - 1) % total;
      if (start <= end) {
        return i >= start && i <= end;
      }
      return i >= start || i <= end;
    };
    if (!inWindow(targetIndex)) {
      // shift start so that targetIndex becomes the last position
      start = (targetIndex - (size - 1) + total) % total;
      setCatWindowStart(start);
    }
  }, [catWindowStart, categories.length]);

  const windowSize = Math.min(5, categories.length);
  const showCategoryArrows = categories.length > 5;
  const displayedCategories = Array.from({ length: windowSize }, (_, i) =>
    categories[(catWindowStart + i) % (categories.length || 1)]
  );

  const handleCatWindowPrev = () => {
    if (!showCategoryArrows) return;
    setCatWindowStart(s => (s - 1 + categories.length) % categories.length);
  };
  const handleCatWindowNext = () => {
    if (!showCategoryArrows) return;
    setCatWindowStart(s => (s + 1) % categories.length);
  };

  // Category icon mapping removed; pills are text-only.

  return (
    <Box sx={{ position: 'relative', minHeight: { xs: 600, md: 520 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
      {/* Zone gauche (hero) */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', borderRadius: 4, p: { xs: 3, md: 5 }, background: 'radial-gradient(circle at 30% 50%, rgba(8,145,178,0.35) 0%, rgba(12,74,110,0.9) 70%)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
        {/* Titre */}
        <Typography variant="overline" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '3px', mb: 1, opacity: 0.9 }}>GAMME DE PRODUITS</Typography>
        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 2, fontSize: { xs: '2rem', md: '2.75rem' }, letterSpacing: '-0.02em' }}>
          {current ? current.titre : 'Catégories'}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 680, lineHeight: 1.6, mb: 3 }}>
          {current?.description || 'Solutions professionnelles pour véhicules et interventions. Parcourez les équipements dédiés.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => current && navigate(`/catalogue?category=${current.id}`)} sx={{ fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, background: 'linear-gradient(90deg,#0077B8,#00ACEA)', '&:hover': { background: 'linear-gradient(90deg,#004F82,#0077B8)' } }}>Produits</Button>
          <Button variant="outlined" onClick={() => navigate('/catalogue')} startIcon={<FaEye />} sx={{ fontWeight: 700, px: 4, py: 1.5, borderRadius: 3, color: 'white', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.08)' } }}>Voir le catalogue</Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => { const ni = (index - 1 + categories.length) % (categories.length || 1); setIndex(ni); ensureWindowContains(ni); }} sx={navBtnSx}><FaChevronLeft /></IconButton>
            <IconButton onClick={() => { const ni = (index + 1) % (categories.length || 1); setIndex(ni); ensureWindowContains(ni); }} sx={navBtnSx}><FaChevronRight /></IconButton>
          </Box>
        </Box>

        {/* Aperçu des produits (3 aléatoires, hors le plus récent) */}
        <Box sx={{ flexGrow: 1, mt: 2 }}>
          {loading && (
            <Typography sx={{ color: 'white', fontWeight: 600 }}>Chargement...</Typography>
          )}
          {!loading && previewProducts.length === 0 && !newestProduct && (
            <Typography sx={{ color: 'white', fontWeight: 600, opacity: 0.7 }}>Aucun produit disponible.</Typography>
          )}
          {!loading && (previewProducts.length > 0 || newestProduct) && (
            <Grid container spacing={2} sx={{ maxWidth: 450 }}>
              {previewProducts.map((p) => (
                <Grid item xs={6} key={p.id}>
                  <Box 
                    sx={{ 
                      position: 'relative', 
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      }
                    }} 
                    onClick={() => navigate(`/products/${p.id}`)}
                  >
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255,255,255,0.08)', 
                        backdropFilter: 'blur(6px)', 
                        borderRadius: '16px', 
                        border: '1px solid rgba(255,255,255,0.2)', 
                        transition: 'all .3s', 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 140,
                        '&:hover': { 
                          bgcolor: 'rgba(255,255,255,0.15)',
                          borderColor: 'rgba(255,255,255,0.4)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.3)'
                        } 
                      }}
                    >
                      <img 
                        src={p._displayImage || 'https://via.placeholder.com/100x100?text=+'} 
                        alt={p.titre} 
                        style={{ 
                          width: 100, 
                          height: 100, 
                          objectFit: 'contain', 
                          filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.4))' 
                        }} 
                      />
                    </Paper>
                    <IconButton 
                      size="small" 
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8, 
                        bgcolor: '#00ACEA', 
                        color: 'white', 
                        width: 32, 
                        height: 32, 
                        boxShadow: '0 4px 12px rgba(0, 172, 234, 0.5)', 
                        '&:hover': { 
                          bgcolor: '#0077B8',
                          transform: 'scale(1.1)'
                        } 
                      }}
                    >
                      <FaEye size={14} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* Carte de mise en avant (produit le plus récent) */}
      <Paper elevation={0} sx={{ width: { xs: '100%', md: 320 }, p: 3, borderRadius: 4, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxShadow: '0 14px 28px rgba(0,0,0,0.10)', border: '1px solid #e2e8f0', alignSelf: { md: 'center' } }}>
        {current && (
          <>
            <Chip label="Nouveau" color="primary" sx={{ position: 'absolute', top: -14, left: 14, fontWeight: 700, boxShadow: '0 10px 24px rgba(2,132,199,0.35)', zIndex: 2 }} />
            <Box sx={{ mb: 2.5 }}>
              <img src={newestProduct?._displayImage || 'https://via.placeholder.com/140x140?text=Produit'} alt={newestProduct?.titre || current.titre} style={{ width: 120, height: 120, objectFit: 'contain' }} onError={(e)=>{e.currentTarget.src='https://via.placeholder.com/140x140?text=Produit';}} />
            </Box>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>{current.titre}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center', mb: 1.5 }}>{newestProduct?.titre || 'Produit à découvrir'}</Typography>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#0ea5e9', mb: 2 }} />
            <Button variant="contained" onClick={() => navigate(`/catalogue?category=${current.id}`)} sx={{ fontWeight: 700, px: 3, py: 1, borderRadius: 3 }}>Voir la catégorie</Button>
          </>
        )}
        {!current && (
          <Typography>Aucune catégorie.</Typography>
        )}
      </Paper>

      {/* Boutons de catégories (fenêtre de 5, flèches centrées) */}
      <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: -110, display: 'flex', alignItems: 'center', gap: 1 }}>
        {showCategoryArrows && (
          <IconButton onClick={handleCatWindowPrev} sx={navBtnSx}><FaChevronLeft /></IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {displayedCategories.map((c, localIndex) => {
            const globalIndex = (catWindowStart + localIndex) % categories.length;
            const active = globalIndex === index;
            return (
              <Paper key={c.id} onClick={() => { setIndex(globalIndex); fetchProductsForCategory(c.id); }} elevation={0} sx={{ cursor: 'pointer', p: 2, height: 100, minWidth: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 3, bgcolor: active ? '#ffffff' : 'rgba(255,255,255,0.08)', color: active ? '#0c4a6e' : 'white', border: active ? '2px solid #0ea5e9' : '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)', transition: 'all .3s', '&:hover': { bgcolor: active ? '#ffffff' : 'rgba(255,255,255,0.15)' } }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>{c.titre}</Typography>
              </Paper>
            );
          })}
        </Box>
        {showCategoryArrows && (
          <IconButton onClick={handleCatWindowNext} sx={navBtnSx}><FaChevronRight /></IconButton>
        )}
      </Box>
    </Box>
  );
};

const navBtnSx = {
  bgcolor: 'rgba(255,255,255,0.15)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.3)',
  backdropFilter: 'blur(6px)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
  width: 44,
  height: 44,
  borderRadius: '50%',
};

export default CategoryShowcase;
